import React, { useCallback } from 'react';

import Autocomplete, { AutocompleteProps } from '@mui/material/Autocomplete';
import Avatar from 'components/mui/Avatar';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import RenderApproversGroup from 'components/Form/Autocomplete/RenderApproversGroup';
import HelperTextError from 'components/mui/Form/TextField/HelperTextError';
import { DEFAULT_GROUP } from 'hooks/approval/useApprovals';

import elements from 'components/elements';

// TODO: HOT TYPE - fix later
interface Approver {
  created_at: string;
  fullName: string;
  group?: string;
  id: string;
  phone: string;
  picture: string | null;
  role: string;
  username: string;
  disabled?: boolean;
}

interface UserFieldProps {
  // Component-specific props
  options: string[];
  approvers: Record<string, Approver>;

  // React Hook Form props
  value: string | null;
  onChange: (value: string | null) => void;
  onBlur: () => void;

  // Separated MUI props
  autocompleteProps?: Partial<
    Omit<AutocompleteProps<string, false, boolean, false>, 'value' | 'onChange' | 'onBlur' | 'options'>
  >;
  textFieldProps?: TextFieldProps;
}

const UserField: React.FC<UserFieldProps> = ({
  options,
  approvers,
  value,
  onChange,
  onBlur,
  textFieldProps,
  autocompleteProps,
}) => {
  const renderOption = useCallback(
    (params: any, username: string) => (
      <MenuItem
        {...params}
        disabled={approvers[username]?.disabled}
        data-element={elements.content.documents.approvals.autocompleteOptions.approvalItem}
      >
        <ListItemText>
          <Stack alignItems="center" direction="row" gap={1}>
            <Avatar src={approvers[username]?.picture} alt={approvers[username]?.fullName || username} />
            <Typography variant="body2">
              {approvers[username] ? `${approvers[username].fullName} (${username})` : username}
            </Typography>
          </Stack>
        </ListItemText>
      </MenuItem>
    ),
    [approvers],
  );

  const renderValue = useCallback(
    (value: string | null, getItemProps: any) => {
      if (!value) return null;

      return (
        <Chip
          avatar={
            <Avatar
              alt={approvers[value]?.fullName || value}
              src={approvers[value]?.picture}
              size={24}
              sx={{ ml: 0.5 }}
            />
          }
          variant="outlined"
          label={approvers[value]?.fullName || value}
          color="primary"
          {...getItemProps()}
        />
      );
    },
    [approvers],
  );

  const getOptionLabel = useCallback(
    (option: string) => {
      return approvers[option] ? `${approvers[option].fullName} ${approvers[option].username}` : option;
    },
    [approvers],
  );

  const getOptionDisabled = useCallback((option: string) => Boolean(approvers[option]?.disabled), [approvers]);

  const groupBy = useCallback(
    (option: string) => {
      return approvers[option]?.group || DEFAULT_GROUP;
    },
    [approvers],
  );

  return (
    <Autocomplete
      autoHighlight
      value={value || null}
      disableClearable
      onChange={(_, newValue) => {
        onChange(newValue);
      }}
      onBlur={onBlur}
      options={options}
      renderOption={renderOption}
      renderValue={renderValue}
      getOptionLabel={getOptionLabel}
      getOptionDisabled={getOptionDisabled}
      renderInput={(params) => {
        return (
          <TextField
            {...params}
            {...textFieldProps}
            error={textFieldProps?.error || false}
            helperText={
              <HelperTextError isError={textFieldProps?.error || false} meta={{ error: textFieldProps?.helperText }} />
            }
          />
        );
      }}
      groupBy={groupBy}
      renderGroup={(params) => <RenderApproversGroup {...params} />}
      {...autocompleteProps}
    />
  );
};

export default UserField;
